import React, { PropsWithChildren, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { Redirect } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import useAuthStore from 'store/userStore'
import { CartLoader } from 'components/Loader/CartLoader'
import { useAuthenticator } from '@aws-amplify/ui-react'

interface IProtectedRoute {
  allowedRoles?: string[]
}

const ProtectedRoute: React.FC<PropsWithChildren<IProtectedRoute>> = ({
  children,
  allowedRoles = []
}) => {
  const { authStatus, signOut } = useAuthenticator() // Use Amplify's auth status
  const { user, logout } = useAuthStore() // Access custom store
  const queryClient = useQueryClient() // React Query client
  const [loading, setLoading] = useState(true) // Manage loading state

  // Synchronize the authentication state on mount
  useEffect(() => {
    const syncAuthState = async () => {
      if (authStatus === 'unauthenticated' && user) {
        console.warn('Auth status mismatch: Clearing stale user data...')
        logout()
        signOut()
        queryClient.clear()
        localStorage.clear()
      }
      setLoading(false) // Sync complete
    }
    syncAuthState()
  }, [authStatus, user, queryClient])

  // Show loader while syncing auth state
  if (loading || authStatus === 'configuring') {
    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 9999
        }}
      >
        <CartLoader />
      </Box>
    )
  }

  // Redirect to login if user is unauthenticated
  if (authStatus === 'unauthenticated' || !user) {
    return <Redirect to="/login" />
  }

  // Check if user has the required role
  const userHasAllowedRole =
    user?.roles?.length &&
    (allowedRoles.length === 0 ||
      user.roles.some((role) => allowedRoles.includes(role)))

  // Redirect to dashboard if user lacks permission
  if (!userHasAllowedRole) {
    return <Redirect to="/dashboard" />
  }

  // Render the children if all checks pass
  return <>{children}</>
}

export default ProtectedRoute
